import React, { useEffect, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import Tooltip from "@mui/material/Tooltip";
import NotesOutlinedIcon from "@mui/icons-material/NotesOutlined";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import BdrModal from "../../../../components/BdrModal/BdrModal";
import "./customerNotesHelperButton.css";
import { GET_CLIENT_NOTES_BY_ID } from "../../../../utils/GraphQL/queries";
import BAGroupTimeFormat from "../../../../utils/formatters/BAGroupTimeFormat";
import HighlightedText from "./HighlightedText";

/**
 * Helper-UI button placed next to a referral-source dropdown. Click to
 * open a read-only modal of the customer's notes with referral keywords
 * (`ref`, `referred`, `referred by`, `referrer`, `referral`) highlighted
 * so the office can quickly find legacy referral info before picking a
 * source from the dropdown. The modal is purely for reading — no
 * selection or save happens here.
 *
 * Lazy-loaded: the GraphQL fetch only fires on first open. Subsequent
 * opens hit the Apollo cache. Renders nothing when `clientId` is falsy
 * (e.g. in CreateClient before a client exists).
 */
const CustomerNotesHelperButton = ({ clientId, size = "small" }) => {
  const [open, setOpen] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const [fetchNotes, { data, loading, error }] = useLazyQuery(
    GET_CLIENT_NOTES_BY_ID,
    // cache-and-network: show cached notes immediately if we've already
    // fetched in this session, AND kick off a fresh network request so
    // recently-added notes show up. Also handles the legacy case where
    // a previous resolver bug cached an empty array.
    { fetchPolicy: "cache-and-network" }
  );

  useEffect(() => {
    if (open && clientId && !hasFetched) {
      fetchNotes({ variables: { clientId } });
      setHasFetched(true);
    }
  }, [open, clientId, hasFetched, fetchNotes]);

  if (!clientId) return null;

  const notes = data?.getClientNotesById || [];
  const sortedNotes = [...notes].sort((a, b) => {
    const aT = Number(a?.dateUpdated || a?.dateCreated || 0);
    const bT = Number(b?.dateUpdated || b?.dateCreated || 0);
    return bT - aT;
  });

  return (
    <>
      <Tooltip title="View customer notes" placement="top">
        <IconButton
          size={size}
          onClick={(e) => {
            e.stopPropagation();
            setOpen(true);
          }}
          sx={{ color: "#fff" }}
        >
          <NotesOutlinedIcon fontSize={size} />
        </IconButton>
      </Tooltip>
      <BdrModal
        open={open}
        onClose={() => setOpen(false)}
        title="Customer Notes"
        variant="panel"
        size="md"
      >
        <div className="cnh-lead">
          Highlights mark referral keywords (<em>ref, referred, referrer,
          referral</em>) — read-only helper to find the source. Pick the
          value from the dropdown after closing.
        </div>
        {loading && (
          <div className="bdr-loading">
            <CircularProgress size={24} sx={{ color: "#fff" }} />
          </div>
        )}
        {error && (
          <div className="bdr-alert bdr-alert--danger bdr-alert--start">
            Could not load notes: {error.message}
          </div>
        )}
        {!loading && !error && sortedNotes.length === 0 && (
          <div className="bdr-empty">No customer notes.</div>
        )}
        {!loading && !error && sortedNotes.length > 0 && (
          <div className="cnh-list">
              {sortedNotes.map((noteObj) => {
                const baseDate =
                  noteObj?.dateUpdated || noteObj?.dateCreated;
                const formatted = BAGroupTimeFormat(baseDate, true, true, true);
                const author = noteObj?.updatedBy || noteObj?.createdBy || "";
                return (
                  <div key={noteObj?._id} className="cnh-note">
                    <div className="cnh-note-meta">
                      <span>{formatted || "—"}</span>
                      {author && <span>· {author}</span>}
                      {noteObj?.dateUpdated && noteObj?.dateCreated &&
                        noteObj.dateUpdated !== noteObj.dateCreated && (
                          <span className="cnh-edited">(edited)</span>
                        )}
                    </div>
                    <div className="cnh-note-body">
                      <HighlightedText text={noteObj?.note || ""} />
                    </div>
                  </div>
                );
              })}
          </div>
        )}
      </BdrModal>
    </>
  );
};

export default CustomerNotesHelperButton;
